/**
 * Platform detection for keyboard shortcuts.
 *
 * The app's navigation / help shortcuts use the conventional "extra" modifier:
 * ⌥ (Option) on macOS, Alt on Windows / Linux. Crucially these are the SAME
 * physical key — macOS Option and Windows Alt both set `event.altKey` — so the
 * matching logic is identical on every platform; only the *displayed* label
 * differs (⌥ vs Alt). We deliberately avoid ⌘ for navigation because ⌘+letter
 * collides with reserved browser / OS shortcuts on macOS (⌘C copy, ⌘H hide,
 * ⌘R reload, …); ⌘K remains the Command Palette and is handled separately.
 *
 * Centralised here so the keymap, the Command Palette badges, and the help
 * modal all agree on which modifier to require and display.
 */

/** True when running on a Mac (or iPad/iPhone) browser. */
export function isMacPlatform(): boolean {
  if (typeof navigator === 'undefined') return false
  const platform = navigator.platform || navigator.userAgent || ''
  return /mac|iphone|ipad|ipod/i.test(platform)
}

/**
 * Display label for the primary shortcut modifier:
 *   - `⌥` on macOS (the Option key)
 *   - `Alt` on Windows / Linux
 */
export function shortcutModifierLabel(): string {
  return isMacPlatform() ? '⌥' : 'Alt'
}

interface ModifierState {
  metaKey: boolean
  ctrlKey: boolean
  altKey: boolean
}

/**
 * True when the event holds the primary shortcut modifier (⌥ on macOS / Alt
 * elsewhere — both `event.altKey`) and no other reserved modifier (⌘ / Ctrl
 * stay reserved for ⌘K and browser / OS shortcuts).
 */
export function hasPrimaryShortcutModifier(event: ModifierState): boolean {
  return event.altKey && !event.metaKey && !event.ctrlKey
}
